// Time-bucketed bar chart for the Inventory Surface's "First seen" / "Last
// seen" tabs. Deliberately NOT canvas (unlike CompositionBar): DIV bars + an
// SVG cumulative-line overlay scale natively on resize/expand with crisp text —
// no canvas DPR/settle dance. Bars are scaled to the tallest bucket; the
// optional cumulative line rides a SECOND scale (0..grand-total) in a normalized
// 0..1 viewBox with a non-scaling stroke so it stays 1.5px at any size. Hover is
// per-bar → a portaled tooltip (same chrome + body-portal as CompositionBar) so
// it never clips inside Explore's transformed columns.
//
// Granularity (day | week | month) is supplied by the caller; the bar/label
// machinery is granularity-agnostic and just thins labels/dots and tightens the
// gap as the bucket count grows, so day-level histograms stay legible.

import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatCompact } from "../lib/renderers.js";
import { TOOLTIP_LAYER } from "../lib/tooltipLayer.js";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TARGET_AXIS_LABELS = 10; // thin x labels toward roughly this many
const MAX_DOTS = 40; // draw cumulative vertices only when buckets are sparse enough

function axisLabel(startMs, g) {
  const d = new Date(startMs);
  if (g === "month") return MON[d.getUTCMonth()];
  return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}`; // day / week → "Jun 6"
}
function tipLabel(startMs, g) {
  const d = new Date(startMs);
  const yy = `’${String(d.getUTCFullYear()).slice(2)}`;
  if (g === "month") return `${MON[d.getUTCMonth()]} ${yy}`;
  if (g === "week") return `Week of ${MON[d.getUTCMonth()]} ${d.getUTCDate()}, ${yy}`;
  return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}, ${yy}`;
}

export default function SeenChart({ buckets = [], granularity = "month", showCumulative = false, valueLabel = "Endpoints" }) {
  const n = buckets.length;
  const [tip, setTip] = useState(null); // { left, top, idx }
  const lastKeyRef = useRef(-1);

  const onEnter = useCallback((i, e) => {
    if (lastKeyRef.current === i) return;
    lastKeyRef.current = i;
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ left: rect.left + rect.width / 2, top: rect.top - 6, idx: i });
  }, []);
  const onLeave = useCallback(() => {
    lastKeyRef.current = -1;
    setTip(null);
  }, []);

  if (!n) {
    return <div className="grid h-full w-full place-items-center text-[12px] text-[color:var(--muted)]">No data</div>;
  }

  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  const maxCumulative = Math.max(1, ...buckets.map((b) => b.cumulative));
  // Tighten inter-bar spacing as the bucket count climbs, but never to zero —
  // a visible gutter keeps day-level views reading as distinct bars rather than
  // a solid filled area, without overflowing (flex shrinks the bars to fit).
  const gapPx = n > 200 ? 1 : n > 120 ? 1.5 : n > 60 ? 2.5 : n > 24 ? 3 : 4;
  // Thin x-axis labels (and year ticks) to ~TARGET_AXIS_LABELS evenly-spaced.
  const stride = Math.max(1, Math.ceil(n / TARGET_AXIS_LABELS));
  const showLabelAt = (i) => i % stride === 0;
  const showDots = showCumulative && n <= MAX_DOTS;

  const linePoints = showCumulative
    ? buckets.map((b, i) => `${(i + 0.5) / n},${1 - b.cumulative / maxCumulative}`).join(" ")
    : "";

  // Year tick under the first shown label and whenever the shown year changes.
  let lastShownYear = null;

  const tipBucket = tip ? buckets[tip.idx] : null;

  return (
    <div className="flex h-full w-full flex-col">
      <div className="relative min-h-0 flex-1">
        {/* Bars — bottom-aligned, one flex column per bucket. */}
        <div className="absolute inset-0 flex items-end px-[1px] pb-[1px]" style={{ gap: `${gapPx}px` }}>
          {buckets.map((b, i) => {
            const active = tip && tip.idx === i;
            return (
              <div
                key={b.startMs}
                className="flex h-full min-w-0 flex-1 items-end"
                onMouseEnter={(e) => onEnter(i, e)}
                onMouseLeave={onLeave}
              >
                <div
                  className="w-full rounded-t-[1px]"
                  style={{
                    height: `${Math.max(b.count ? 2 : 0, (b.count / maxCount) * 100)}%`,
                    backgroundColor: "var(--green)",
                    opacity: active ? 1 : 0.82,
                  }}
                />
              </div>
            );
          })}
        </div>
        {/* Cumulative rollout line (first-seen only). */}
        {showCumulative ? (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
            <polyline
              points={linePoints}
              fill="none"
              stroke="var(--ink)"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity="0.7"
            />
          </svg>
        ) : null}
        {showDots
          ? buckets.map((b, i) => (
              <span
                key={`dot-${b.startMs}`}
                className="pointer-events-none absolute h-[4px] w-[4px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[color:var(--ink)]"
                style={{ left: `${((i + 0.5) / n) * 100}%`, top: `${(1 - b.cumulative / maxCumulative) * 100}%` }}
                aria-hidden="true"
              />
            ))
          : null}
      </div>

      {/* X axis — thinned period labels + a year tick when the shown year turns. */}
      <div className="mt-[3px] flex px-[1px]" style={{ gap: `${gapPx}px` }}>
        {buckets.map((b, i) => {
          if (!showLabelAt(i)) return <div key={b.startMs} className="flex-1" />;
          const year = new Date(b.startMs).getUTCFullYear();
          const showYear = lastShownYear !== year;
          lastShownYear = year;
          return (
            // Thinned labels are spaced `stride` cells apart, so let the text
            // overflow its (narrow, at day grain) cell into the empty neighbors
            // instead of truncating to "J…".
            <div key={b.startMs} className="flex-1 text-center">
              <div className="whitespace-nowrap text-[9px] leading-[1.1] text-[color:var(--muted)]">{axisLabel(b.startMs, granularity)}</div>
              <div className={["text-[8px] leading-[1.1]", showYear ? "text-[color:var(--muted-soft)]" : "opacity-0"].join(" ")}>
                {showYear ? `’${String(year).slice(2)}` : "·"}
              </div>
            </div>
          );
        })}
      </div>

      {tipBucket ? createPortal(
        <div
          role="tooltip"
          className={`pointer-events-none fixed ${TOOLTIP_LAYER} grid min-w-[128px] gap-[3px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[5px] text-[11px] leading-[1.2] text-[color:var(--ink)] shadow-lg`}
          style={{ left: `${tip.left}px`, top: `${tip.top}px`, transform: "translate(-50%, -100%)" }}
        >
          <div className="font-medium">{tipLabel(tipBucket.startMs, granularity)}</div>
          <div className="flex items-baseline justify-between gap-[12px]">
            <span className="text-[color:var(--muted)]">{valueLabel}</span>
            <span className="tabular-nums">{formatCompact(tipBucket.count)}</span>
          </div>
          {showCumulative ? (
            <div className="flex items-baseline justify-between gap-[12px]">
              <span className="text-[color:var(--muted)]">Cumulative</span>
              <span className="tabular-nums">{formatCompact(tipBucket.cumulative)}</span>
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
